import React, { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    IconButton,
    Box,
    Typography,
    Grid,
    Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import emailjs from '@emailjs/browser';

const EnquiryFormModal = ({ page, open, setOpen }) => {
    const [snakeBar, setSnakeBar] = useState(false);

    const [formData, setFormData] = useState({
        fullName: '',
        contactNumber: '',
        email: '',
        travellingFrom: '',
        travellingTo: '',
        arrivalDate: '',
        returnDate: '',
        query: ''
    });

    const [errors, setErrors] = useState({});
    const form = useRef('');

    const fieldRefs = {
        fullName: useRef(null),
        contactNumber: useRef(null),
        email: useRef(null),
        travellingFrom: useRef(null),
        travellingTo: useRef(null),
        arrivalDate: useRef(null),
        returnDate: useRef(null),
    };

    const Alert = React.forwardRef(function Alert(props, ref) {
        return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    // 🔹 Validation Logic
    const validateField = (name, value) => {
        let message = "";

        switch (name) {
            case "fullName":
                if (!value.trim()) message = "Full name is required";
                break;
            case "contactNumber":
                if (!value.trim()) message = "Mobile number is required";
                else if (!/^\d{10}$/.test(value)) message = "Enter a valid number";
                break;
            case "email":
                if (!value.trim()) message = "Email is required";
                else if (!/\S+@\S+\.\S+/.test(value)) message = "Enter a valid email";
                break;
            case "travellingFrom":
                if (!value.trim()) message = "Travelling from is required";
                break;
            case "travellingTo":
                if (!value.trim()) message = "Travelling to is required";
                break;
            case "arrivalDate":
                if (!value.trim()) {
                    message = "Arrival Date is required";
                } else {
                    const today = new Date().setHours(0, 0, 0, 0);
                    const arrival = new Date(value).setHours(0, 0, 0, 0);
                    if (arrival < today) {
                        message = "Arrival date cannot be in the past";
                    }
                }
                break;
            case "returnDate":
                if (!value.trim()) {
                    message = "Return Date is required";
                } else {
                    const arrival = new Date(formData.arrivalDate).setHours(0, 0, 0, 0);
                    const returnDate = new Date(value).setHours(0, 0, 0, 0);
                    if (!formData.arrivalDate) {
                        message = "Please select arrival date first";
                    } else if (returnDate < arrival) {
                        message = "Return date cannot be before arrival date";
                    }
                }
                break;
            default:
                break;
        }
        return message;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        const errorMsg = validateField(name, value);
        setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    };

    const validateForm = () => {
        let tempErrors = {};
        Object.keys(formData).forEach((key) => {
            const msg = validateField(key, formData[key]);
            if (msg) tempErrors[key] = msg;
        });
        setErrors(tempErrors);

        if (Object.keys(tempErrors).length > 0) {
            const firstErrorField = Object.keys(tempErrors)[0];
            if (fieldRefs[firstErrorField]?.current) {
                fieldRefs[firstErrorField].current.focus();
            }
            return false;
        }

        return true;
    };

    const sendEmail = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        emailjs.sendForm('service_juwg5yr', 'template_wpjqmaa', form.current, 'mhKR7MXu_HnJfHnc9')
            .then(() => {
                setSnakeBar(true);
                setFormData({
                    fullName: '',
                    contactNumber: '',
                    email: '',
                    travellingFrom: '',
                    travellingTo: '',
                    arrivalDate: '',
                    returnDate: '',
                    query: ''
                });
                setErrors({});
            }, (error) => {
                console.log(error.text);
            });
    };

    const handleCloseSnakebar = (reason) => {
        if (reason === 'clickaway') return;
        setSnakeBar(false);
    };

    useEffect(() => {
        if (page === "Home") {
            const timer = setTimeout(() => setOpen(true), 5000);
            return () => clearTimeout(timer);
        }
    }, [page, setOpen]);

    const inputSx = {
        '& .MuiInputBase-root': { height: 40 },
        '& input': { padding: '10px 10px' },
        '& .MuiInputAdornment-root': { marginTop: '-2px' }
    };

    return (
        <>
            <Snackbar open={snakeBar} autoHideDuration={6000} onClose={handleCloseSnakebar}>
                <Alert onClose={handleCloseSnakebar} severity="success" sx={{ width: '100%' }}>
                    Your Query has been sent successfully!
                </Alert>
            </Snackbar>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Plan Your Dream Trip
                    <IconButton
                        onClick={() => setOpen(false)}
                        sx={{ position: 'absolute', right: 16, top: 16 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body1" align="center" sx={{ mb: 3, color: '#5b5b5b' 
                    , fontSize: {
                        xs: '0.9rem',   // small screens
                        sm: '1rem',     // tablets
                        md: '1.1rem',   // desktops
                    },

                    }}>
                        Tell us a bit about your trip and our travel consultants will get back to you shortly.
                    </Typography>

                    <Box component="form" onSubmit={sendEmail} ref={form}>
                        <Grid container spacing={2} size={{ xs: 12 }}>
                            {/* Full Name */}
                            <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                                <TextField
                                    fullWidth label="Full Name" name="fullName"
                                    value={formData.fullName} onChange={handleChange}
                                    error={!!errors.fullName} helperText={errors.fullName}
                                    inputRef={fieldRefs.fullName}
                                    InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1 }} /> }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Contact Number */}
                            <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                                <TextField
                                    fullWidth label="Mobile Number" name="contactNumber"
                                    type="tel" value={formData.contactNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value) && value.length <= 10) {
                                            handleChange(e);
                                        }
                                    }}
                                    error={!!errors.contactNumber} helperText={errors.contactNumber}
                                    inputRef={fieldRefs.contactNumber}
                                    InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1 }} /> }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Email */}
                            <Grid item size={{ xs: 12, md: 12, lg: 12 }}>
                                <TextField
                                    fullWidth label="Email Address" name="email"
                                    type="email" value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email} helperText={errors.email}
                                    inputRef={fieldRefs.email}
                                    InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1 }} /> }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Travelling From */}
                            <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                                <TextField
                                    fullWidth label="From" name="travellingFrom"
                                    value={formData.travellingFrom}
                                    onChange={handleChange}
                                    error={!!errors.travellingFrom} helperText={errors.travellingFrom}
                                    inputRef={fieldRefs.travellingFrom}
                                    InputProps={{ startAdornment: <FlightTakeoffIcon sx={{ mr: 1 }} /> }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Travelling To */}
                            <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                                <TextField
                                    fullWidth label="To" name="travellingTo"
                                    value={formData.travellingTo}
                                    onChange={handleChange}
                                    error={!!errors.travellingTo} helperText={errors.travellingTo}
                                    inputRef={fieldRefs.travellingTo}
                                    InputProps={{ startAdornment: <FlightLandIcon sx={{ mr: 1 }} /> }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Arrival Date */}
                            <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                                <TextField
                                    fullWidth label="Arrival Date" name="arrivalDate"
                                    type="date" value={formData.arrivalDate}
                                    onChange={handleChange}
                                    error={!!errors.arrivalDate} helperText={errors.arrivalDate}
                                    inputRef={fieldRefs.arrivalDate}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: new Date().toISOString().split('T')[0]
                                    }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Return Date */}
                            <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                                <TextField
                                    fullWidth label="Return Date" name="returnDate"
                                    type="date" value={formData.returnDate}
                                    onChange={handleChange}
                                    error={!!errors.returnDate} helperText={errors.returnDate}
                                    inputRef={fieldRefs.returnDate}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: formData.arrivalDate || new Date().toISOString().split('T')[0]
                                    }}
                                    sx={inputSx}
                                />
                            </Grid>

                            {/* Query */}
                            <Grid item size={{ xs: 12, md: 12, lg: 12 }}>
                                <TextField
                                    fullWidth multiline rows={2} variant="outlined"
                                    label="Ask Your Travel Consultant a Question"
                                    name="query" value={formData.query}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* Submit */}
                            <Grid item xs={12}>
                                <Button
                                    type="submit" fullWidth variant="contained"
                                    sx={{
                                        mt: 1, py: 1.5,
                                        background: 'linear-gradient(to right, #e96443, #904e95)',
                                        fontWeight: 'bold', fontSize: '16px', textTransform: 'none',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #fd3a69, #ff7e5f)'
                                        }
                                    }}
                                >
                                    Submit Enquiry
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EnquiryFormModal;
